import { useTranslation } from 'react-i18next';
import { ReactElement, useMemo } from 'react';
import { DrawerPanel } from '@dmc/ui';
import { CasFormFilePicker } from '@cas/components/inputs/cas-form-file-picker/CasFormFilePicker';
import { ModelName, QueryTag } from '@casops/store/api';
import { TagDescription } from '@reduxjs/toolkit/query';
import {
  FileUploadModel,
  ValidNameRegex,
  ValidVersionRegex,
} from '@casops/store/apis/commonModels';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import useFileUploadForm from '@casops/components/layout/model-form/useFileUploadForm';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { CasFormDateTimePicker } from '@casops/components/inputs/cas-form-date-time-picker/CasFormDateTimePicker';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { ServerErrorFields } from '@dmc/ui';

const NAME_MAX = 50;
const VERSION_MAX = 50;
interface FormModel extends FileUploadModel {
  name: string;
  at: string;
  ver: string;
}

const modelName = ModelName.AssetDeviceFile;

const initialValues: FormModel = {
  name: '',
  at: new Date().toISOString(),
  ver: '',
  file: undefined,
};

const serverErrorFields: ServerErrorFields<FormModel> = {
  name: 'Name',
  ver: 'Version',
};

export function DeviceFileUploadPanel(): ReactElement {
  const { t } = useTranslation();
  const { assetId } = useParams();
  const endpointUrl = `api/assets/${assetId}/files`;
  const tagsToClear: TagDescription<QueryTag>[] = [{ type: QueryTag.AssetRegistered, id: assetId }];

  const fieldsSchema = useMemo(() => {
    return z.object({
      name: z
        .string()
        .min(1, { message: t('Validation.SpecifyFileName') })
        .max(NAME_MAX, { message: t('Validation.FileNameTooLong', { max: NAME_MAX }) })
        .regex(ValidNameRegex, { message: t('Validation.InvalidCharacters') }),
      at: z
        .string()
        .min(1, { message: t('Validation.SpecifyDate') })
        .refine(
          val => {
            // Check if it's a valid ISO date-time string
            return !isNaN(Date.parse(val));
          },
          { message: t('Validation.SpecifyDate') },
        ),
      ver: z
        .string()
        .min(1, { message: t('Validation.SpecifyVersion') })
        .max(VERSION_MAX, { message: t('Validation.VersionTooLong', { max: VERSION_MAX }) })
        .regex(ValidVersionRegex, { message: t('Validation.InvalidCharacters') }),
    });
  }, [t]);

  const formProps = useFileUploadForm<FormModel>({
    endpointUrl,
    tagsToClear,
    defaultValues: initialValues,
    successMessage: t(`Model.Create.Success`, { modelName }),
    fieldsSchema,
    serverErrorFields,
  });

  return (
    <DrawerPanel header={t('UploadFiles')}>
      <ModelForm {...formProps} submitLabel={t('Upload')}>
        <CasFormInput
          key="name"
          control={formProps.control}
          name="name"
          label={t('Fields.FileName')}
        />
        <CasFormInput
          key="ver"
          control={formProps.control}
          name="ver"
          label={t('Fields.Version')}
        />
        <CasFormDateTimePicker key="at" control={formProps.control} name="at" />
        <CasFormFilePicker control={formProps.control} name="file" accept="" minHeight="200px" />
      </ModelForm>
    </DrawerPanel>
  );
}

export default DeviceFileUploadPanel;
